/**
 * 코드 기반 라우트 트리 — 생성 파일 없이 tsc 가 바로 검사한다.
 *
 * 영역(260917-admin-area-split · 261013-account-brand-membership): 사용자는 /app, 운영자는 /admin.
 * **두 접두 밖은 전부 공개 화면**이고 공개 화면은 root 직계로 둔다 — 영역 레이아웃(가드) 밖이라 내 계정을 묻지 않는다.
 * 옛 대행사 영역 주소(/agency/*)는 /app 의 같은 화면으로 넘긴다(북마크·메일 링크).
 * 두 영역에 **같은 컴포넌트**가 걸린 화면은 비밀번호뿐이다 — 그 링크는 lib/paths.ts 의 표(`pathsFor(role)`)로 만든다.
 * 첫 화면은 갈라졌고(/app=ReportPage · /admin=OverviewPage) 질문 상세·샘플 상세는 /admin 에만 있다 (260921-client-report-chart-only).
 */
import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';
import { AccountPage } from './routes/account.tsx';
import { ArticleDetailPage, ArticlesPage } from './routes/articles.tsx';
import { ContractDetailPage, ContractNewPage, ContractsPage } from './routes/contracts.tsx';
import { VideosPage } from './routes/videos.tsx';
import { AdminLoginPage, LoginPage, PasswordPage } from './routes/auth.tsx';
import { CitationsPage } from './routes/citations.tsx';
import { BrandDetailPage, BrandsPage } from './routes/brands.tsx';
import { ReportMessagesPage } from './routes/report-messages.tsx';
import { HealthPage } from './routes/health.tsx';
import { InviteAcceptPage } from './routes/invite.tsx';
import { PayLinkPage } from './routes/pay.tsx';
import { ReportSharePage } from './routes/report-share.tsx';
import { AdminLayout, AppLayout } from './routes/layouts.tsx';
import { MyBrandDetailPage, MyBrandsPage } from './routes/my-brands.tsx';
import { MarketingConsentPage, SubscriptionTermsPage, PrivacyPage, TermsPage } from './routes/legal.tsx';
import { NotFound } from './routes/not-found.tsx';
import { OverviewPage } from './routes/overview.tsx';
import { ReportPage } from './routes/report.tsx';
import { ReviewDetailPage, ReviewListPage } from './routes/review.tsx';
import { QueriesPage } from './routes/queries.tsx';
import { QueryDetailPage } from './routes/query-detail.tsx';
import { QuerySamplesPage, validateQuerySamplesSearch } from './routes/query-samples.tsx';
import { SampleDetailPage } from './routes/sample-detail.tsx';
import { SignupPage } from './routes/signup.tsx';
import { SignupsPage } from './routes/signups.tsx';
import { GeoCaseDetailPage, GeoCasesPage } from './routes/geo-cases.tsx';
import { SeoPage } from './routes/seo.tsx';
import { SitesPage } from './routes/sites.tsx';
import { AgenciesPage, AgencyDetailPage } from './routes/agencies.tsx';
import { UsersPage } from './routes/users.tsx';
import { VerifyEmailPage } from './routes/verify-email.tsx';
import { WorkersPage } from './routes/workers.tsx';
import { WorksPage } from './routes/works.tsx';
import { AdminBillingPage, BillingPage } from './routes/billing.tsx';
import { Redirect } from './components/Redirect.tsx';
import { validateReportSearch } from './lib/appBrand.ts';

const root = createRootRoute();

// 경로는 리터럴로 — 헬퍼로 감싸 string 으로 넓히면 Link·useParams 의 경로 타입 검사가 사라진다
const index = createRoute({
  getParentRoute: () => root,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/app', replace: true });
  },
});

// ── 공개 ────────────────────────────────────────────────────────────────
const login = createRoute({ getParentRoute: () => root, path: '/login', component: LoginPage });
// 접두는 /admin 이지만 root 직계다 — 운영자 레이아웃(가드)이 끼지 않는다
const adminLogin = createRoute({ getParentRoute: () => root, path: '/admin/login', component: AdminLoginPage });
const signup = createRoute({ getParentRoute: () => root, path: '/signup', component: SignupPage });
const verifyEmail = createRoute({
  getParentRoute: () => root,
  path: '/signup/verify-email',
  component: VerifyEmailPage,
});
const terms = createRoute({ getParentRoute: () => root, path: '/terms', component: TermsPage });
const privacy = createRoute({ getParentRoute: () => root, path: '/privacy', component: PrivacyPage });
const marketing = createRoute({ getParentRoute: () => root, path: '/marketing', component: MarketingConsentPage });
// 구독 서비스 이용약관 — 공개 화면이다(시작 화면이 새 창으로 연다. 로그인 없이도 읽힌다)
const subscriptionTerms = createRoute({
  getParentRoute: () => root,
  path: '/subscription-terms',
  component: SubscriptionTermsPage,
});
// 브랜드 초대 받는 쪽 — 로그인 전에 열려도 토큰을 잃지 않게 공개 화면이다
const invite = createRoute({ getParentRoute: () => root, path: '/invite', component: InviteAcceptPage });
// 결제 링크 — 로그인 없이 카드 등록·결제(261020-pay-link). 공개 화면이라 root 직계다
const payLink = createRoute({ getParentRoute: () => root, path: '/pay', component: PayLinkPage });
// 월간 보고서 공유 링크 — 알림톡 버튼이 연다. 로그인 없이 발송 때 고정한 보고서(261030-report-auto-dispatch). 공개 화면이라 root 직계다
const reportShare = createRoute({ getParentRoute: () => root, path: '/r', component: ReportSharePage });

/**
 * 옛 대행사 영역 주소 → 사용자 영역의 같은 화면 (261013-account-brand-membership). 북마크·이미 보낸 메일 링크용이다.
 * 고객 목록·초대는 「브랜드」, 설정은 「계정」, 로그인·가입은 하나로 합쳤다. 나머지는 같은 경로를 /app 아래에서 연다
 */
export function agencyRedirect(rest: string): string {
  const path = rest.replace(/^\/+|\/+$/g, '');
  if (path === '' || path === 'invites' || path.startsWith('brands')) return '/app/brands';
  if (path === 'settings') return '/app/account';
  if (path === 'login') return '/login';
  if (path === 'signup') return '/signup';
  return `/app/${path}`;
}
const agencyRoot = createRoute({
  getParentRoute: () => root,
  path: '/agency',
  beforeLoad: () => {
    throw redirect({ href: agencyRedirect(''), replace: true });
  },
});
const agencyAny = createRoute({
  getParentRoute: () => root,
  path: '/agency/$',
  beforeLoad: ({ params }) => {
    throw redirect({ href: agencyRedirect(params._splat ?? ''), replace: true });
  },
});

// ── 사용자 (/app) ───────────────────────────────────────────────────────
const app = createRoute({ getParentRoute: () => root, path: '/app', component: AppLayout });
// 고객 영역은 **전용 화면**을 쓴다 — 운영자 표(overview.tsx)와 한 컴포넌트에 역할 분기를 쌓지 않는다
// 보고서는 `/app/report?brand=<브랜드>` — 브랜드가 주소에 있어 공유된다(운영자 결정 2026-10-13).
// `/app` 은 임시로 보고서로 넘긴다(첫 화면이 따로 생기면 바꾼다)
const appHome = createRoute({
  getParentRoute: () => app,
  path: '/',
  component: () => <Redirect go={(n) => n({ to: '/app/report', replace: true })} />,
});
const appReport = createRoute({
  getParentRoute: () => app,
  path: '/report',
  component: ReportPage,
  validateSearch: validateReportSearch,
});
const appBrand = createRoute({ getParentRoute: () => app, path: '/brands/$id', component: MyBrandDetailPage });
// 섹션 전용 — 첫 화면 레일에서 넘친 것을 **그리드**로 전부 본다(카드 40장짜리 레일은 가로로 한없이 길다).
// ★ 상한 없는 "전부" 화면(`/app/works`)은 두지 않는다 — `/app` 에서 그래프와 상한만 뺀 것이라 군더더기였다
const appWorksSection = createRoute({
  getParentRoute: () => app,
  path: '/works/$section',
  component: WorksPage,
  // 보고서에서 따라온 브랜드(`?brand=`) — 다브랜드 고객의 전체 보기가 같은 브랜드를 본다
  validateSearch: validateReportSearch,
});
const appArticles = createRoute({ getParentRoute: () => app, path: '/articles', component: ReviewListPage });
const appArticle = createRoute({ getParentRoute: () => app, path: '/articles/$id', component: ReviewDetailPage });
const appPassword = createRoute({ getParentRoute: () => app, path: '/account/password', component: PasswordPage });
// 계정 — 이름·회사명·연락처·탈퇴 (261013-account-brand-membership)
const appAccount = createRoute({ getParentRoute: () => app, path: '/account', component: AccountPage });
// 내 브랜드 — 역할·소속자·초대·브랜드 추가 (261013-account-brand-membership)
const appBrands = createRoute({ getParentRoute: () => app, path: '/brands', component: MyBrandsPage });
// 구독 관리 — 약관(구독 서비스 이용약관)이 가리키는 메뉴 (261011-subscription-billing-payup)
const appBilling = createRoute({ getParentRoute: () => app, path: '/billing', component: BillingPage });

// ── 운영자 (/admin) ─────────────────────────────────────────────────────
const admin = createRoute({ getParentRoute: () => root, path: '/admin', component: AdminLayout });
const adminHome = createRoute({ getParentRoute: () => admin, path: '/', component: OverviewPage });
const adminBrands = createRoute({ getParentRoute: () => admin, path: '/brands', component: BrandsPage });
const adminBrand = createRoute({ getParentRoute: () => admin, path: '/brands/$id', component: BrandDetailPage });
const adminQuery = createRoute({ getParentRoute: () => admin, path: '/queries/$id', component: QueryDetailPage });
const adminQuerySamples = createRoute({
  getParentRoute: () => admin,
  path: '/queries/$id/samples',
  component: QuerySamplesPage,
  validateSearch: validateQuerySamplesSearch,
});
const adminSample = createRoute({ getParentRoute: () => admin, path: '/samples/$id', component: SampleDetailPage });
const adminPassword = createRoute({ getParentRoute: () => admin, path: '/account/password', component: PasswordPage });
const adminQueries = createRoute({ getParentRoute: () => admin, path: '/queries', component: QueriesPage });
const adminSignups = createRoute({ getParentRoute: () => admin, path: '/signups', component: SignupsPage });
// 인터뷰 준비 케이스 — geo 스레드 알림의 링크가 상세로 온다 (261023-geo-watch-agent)
const adminGeoCases = createRoute({ getParentRoute: () => admin, path: '/geo-cases', component: GeoCasesPage });
const adminGeoCase = createRoute({ getParentRoute: () => admin, path: '/geo-cases/$id', component: GeoCaseDetailPage });
const adminUsers = createRoute({ getParentRoute: () => admin, path: '/users', component: UsersPage });
// 대행사 — 읽기 전용(쓰기는 cli agency) · 261025-agency-white-label
const adminAgencies = createRoute({ getParentRoute: () => admin, path: '/agencies', component: AgenciesPage });
const adminAgency = createRoute({ getParentRoute: () => admin, path: '/agencies/$id', component: AgencyDetailPage });
const adminSites = createRoute({ getParentRoute: () => admin, path: '/sites', component: SitesPage });
const adminCitations = createRoute({ getParentRoute: () => admin, path: '/citations', component: CitationsPage });
const adminSeo = createRoute({ getParentRoute: () => admin, path: '/seo', component: SeoPage });
const adminWorkers = createRoute({ getParentRoute: () => admin, path: '/workers', component: WorkersPage });
const adminHealth = createRoute({ getParentRoute: () => admin, path: '/health', component: HealthPage });
const adminArticles = createRoute({ getParentRoute: () => admin, path: '/articles', component: ArticlesPage });
const adminContracts = createRoute({ getParentRoute: () => admin, path: '/contracts', component: ContractsPage });
const adminVideos = createRoute({ getParentRoute: () => admin, path: '/videos', component: VideosPage });
const adminBilling = createRoute({ getParentRoute: () => admin, path: '/billing', component: AdminBillingPage });
// 보고서 발송 — 업체별 보고서 이미지 + 카톡 안내문 (261020-report-message)
const adminReportMessages = createRoute({
  getParentRoute: () => admin,
  path: '/report-messages',
  component: ReportMessagesPage,
});
const adminContractNew = createRoute({
  getParentRoute: () => admin,
  path: '/contracts/new',
  component: ContractNewPage,
});
const adminContract = createRoute({
  getParentRoute: () => admin,
  path: '/contracts/$id',
  component: ContractDetailPage,
});
const adminArticle = createRoute({ getParentRoute: () => admin, path: '/articles/$id', component: ArticleDetailPage });

const routeTree = root.addChildren([
  index,
  login,
  adminLogin,
  signup,
  verifyEmail,
  terms,
  privacy,
  marketing,
  subscriptionTerms,
  invite,
  payLink,
  reportShare,
  agencyRoot,
  agencyAny,
  // 질문 상세·샘플 상세는 **운영자 영역에만** 있다 (260921-client-report-chart-only).
  // 첫 화면이 질문마다 수치와 추이를 이미 보여 주므로 드릴다운이 없고, 링크를 뺀 김에 주소로도 닿지
  // 않게 라우트째 뺐다. 대리 로그인 중인 운영자도 /app 에서는 못 본다 — 자기 /admin 탭에서 본다.
  // ★ 계약(metrics.sample)은 여전히 scoped 다 — 동선을 닫은 것이지 권한을 막은 것이 아니다
  app.addChildren([
    appHome,
    appReport,
    appWorksSection,
    appBrand,
    appArticles,
    appArticle,
    appPassword,
    appAccount,
    appBrands,
    appBilling,
  ]),
  admin.addChildren([
    adminHome,
    adminBrands,
    adminBrand,
    adminQuery,
    adminQuerySamples,
    adminSample,
    adminPassword,
    adminQueries,
    adminSignups,
    adminGeoCases,
    adminGeoCase,
    adminUsers,
    adminAgencies,
    adminAgency,
    adminSites,
    adminCitations,
    adminSeo,
    adminWorkers,
    adminHealth,
    adminArticles,
    adminArticle,
    adminContracts,
    adminVideos,
    adminBilling,
    adminReportMessages,
    adminContractNew,
    adminContract,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // 영역을 pathname 접두로 판정한다(lib/paths.ts) — /ADMIN/… 이 매치되면 그 판정과 어긋난다
  caseSensitive: true,
  defaultNotFoundComponent: NotFound,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
